<template>
  <div class="test-results">
    <div class="card">
      <div class="result-badge" :style="{ background: p1.bg, color: p1.color }">
        Perfil: {{ p1.short }} + {{ p2.short }}
      </div>
      <div class="result-name">{{ aiAnalysis.PerfilTitulo || `${p1.name} · ${p2.name}` }}</div>
      <div class="result-sub">{{ aiAnalysis.PerfilDescripcion }}</div>
      <div class="score-section">
        <div class="score-title">Tu perfil RIASEC completo</div>
        <div id="r-scores">
          <div class="score-row" v-for="[k, v] in scoreData.sorted" :key="k">
            <div class="score-label" :style="{
              fontWeight: k === scoreData.top1 ? '700' : '500',
              color: k === scoreData.top1 || k === scoreData.top2 ? RIASEC_INFO[k].color : 'var(--text-sec)'
            }">
              {{ RIASEC_INFO[k].short }}
            </div>
            <div class="score-track">
              <div class="score-fill" :style="{
                width: Math.round((v/scoreData.maxVal)*100) + '%',
                background: RIASEC_INFO[k].color,
                opacity: k === scoreData.top1 ? 1 : k === scoreData.top2 ? 0.75 : 0.45
              }"></div>
            </div>
            <div class="score-pct" :style="{ color: k === scoreData.top1 ? RIASEC_INFO[k].color : 'var(--text-sec)' }">
              {{ Math.round((v/scoreData.maxVal)*100) }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Análisis IA -->
    <div id="r-analysis">
      <div class="analysis-block" v-if="aiAnalysis.Fortalezas && aiAnalysis.Fortalezas.length">
        <h4>
          <span class="analysis-icon" :style="{ background: p1.bg }">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 2l2 4 4.5.65-3.25 3.17.77 4.49L8 12.1l-4.02 2.11.77-4.49L1.5 6.65 6 6z" :fill="p1.color"/></svg>
          </span>Tus fortalezas
        </h4>
        <div class="analysis-text">
          <div v-for="(f, i) in aiAnalysis.Fortalezas" :key="i" style="margin-bottom:.5rem">· <strong>{{ f }}</strong></div>
        </div>
      </div>

      <div class="analysis-block" v-if="aiAnalysis.Retos && aiAnalysis.Retos.length">
        <h4>
          <span class="analysis-icon" style="background:#EAF3DE">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 1v7M8 11v1" stroke="#27500A" stroke-width="2" stroke-linecap="round"/><circle cx="8" cy="8" r="7" stroke="#27500A" stroke-width="1.5"/></svg>
          </span>Áreas de desarrollo
        </h4>
        <div class="analysis-text">
          <div v-for="(r, i) in aiAnalysis.Retos" :key="i" style="margin-bottom:.5rem">· {{ r }}</div>
        </div>
      </div>

      <div class="analysis-block" v-if="aiAnalysis.MensajePersonal" style="background:var(--gold-bg);border-color:var(--gold)">
        <h4 style="color:#633806">
          <span class="analysis-icon" style="background:rgba(223,193,78,.3)">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8c0-2.76 2.24-5 5-5s5 2.24 5 5-2.24 5-5 5-5-2.24-5-5z" stroke="#633806" stroke-width="1.5"/><path d="M8 5v3l2 2" stroke="#633806" stroke-width="1.5" stroke-linecap="round"/></svg>
          </span>Mensaje de tu orientador
        </h4>
        <div class="analysis-text" style="color:#633806">{{ aiAnalysis.MensajePersonal }}</div>
      </div>
    </div>

    <!-- Empleos del futuro -->
    <div style="margin-bottom:.5rem" v-if="aiAnalysis.Empleos && aiAnalysis.Empleos.length">
      <div style="font-size:11px;font-weight:700;letter-spacing:.08em;color:var(--text-sec);text-transform:uppercase;margin-bottom:.75rem;padding-bottom:6px;border-bottom:1.5px solid var(--gold)">
        Empleos del futuro para tu perfil
      </div>
      <div id="r-jobs">
        <div class="job-card" v-for="(job, i) in aiAnalysis.Empleos" :key="i" :class="{ featured: i === 0 }">
          <div class="job-header">
            <div class="job-title">{{ job.Titulo }}</div>
            <span class="job-badge" :class="i === 0 ? 'hot' : 'new'">{{ i === 0 ? '★ Top match' : 'Recomendado' }}</span>
          </div>
          <div class="job-stat" style="margin-bottom:.5rem;color:var(--text-sec);font-size:13px;line-height:1.5">{{ job.PorQue }}</div>
          <div class="job-stat"><strong>Mercado: </strong>{{ job.Mercado }}</div>
          <div class="job-stat"><strong>IA + Futuro: </strong>{{ job.ComoCambiaTech }}</div>
          <div :style="`margin-top:.6rem;padding:.5rem .75rem;background:${i === 0 ? 'rgba(223,193,78,.25)' : 'var(--bg)'};border-radius:8px;font-size:12px;color:var(--carbon)`">
            <strong>Formación en IBERO: </strong>{{ job.FormacionIbero }}
          </div>
        </div>
      </div>
    </div>

    <!-- Programas IBERO -->
    <div class="card">
      <div class="score-title">Programas recomendados · IBERO</div>
      <div style="font-size:12px;font-weight:600;color:var(--text-sec);margin-bottom:.5rem;text-transform:uppercase;letter-spacing:.05em;font-size:10px">Pregrado</div>
      <div class="prog-list" id="r-pre">
        <div class="prog-item" v-for="(p, i) in prePrograms" :key="i" :class="i === 0 ? 'top' : 'rest'">
          <span class="prog-name">{{ p }}</span>
          <div style="display:flex;align-items:center;gap:6px">
            <span class="prog-meta">Virtual</span>
            <span class="prog-star" v-if="i === 0">★ Top</span>
          </div>
        </div>
      </div>
      <div style="font-size:12px;font-weight:600;color:var(--text-sec);margin-top:1rem;margin-bottom:.5rem;text-transform:uppercase;letter-spacing:.05em;font-size:10px">Posgrado</div>
      <div class="prog-list" id="r-pos">
        <div class="prog-item" v-for="(p, i) in posPrograms" :key="i" :class="i === 0 ? 'top' : 'rest'">
          <span class="prog-name">{{ p }}</span>
          <div style="display:flex;align-items:center;gap:6px">
            <span class="prog-meta">Virtual</span>
            <span class="prog-star" v-if="i === 0">★ Top</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Next step -->
    <div class="analysis-block" style="border:1.5px solid var(--carbon)" v-if="aiAnalysis.ProximoPaso">
      <h4>
        <span class="analysis-icon" style="background:var(--carbon)">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>Tu próximo paso esta semana
      </h4>
      <div class="analysis-text"><strong>{{ aiAnalysis.ProximoPaso }}</strong></div>
    </div>

    <div class="cta-row">
      <button class="btn-ghost" @click="$emit('restart')">Reiniciar test</button>
      <button class="btn-outline" @click="genPDF()" :disabled="isGeneratingPDF">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="vertical-align:middle;margin-right:5px">
          <path d="M8 1v9M8 10l-3-3M8 10l3-3M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ isGeneratingPDF ? 'Descargando...' : 'Descargar PDF' }}
      </button>
      <button target="_blank" class="btn-primary gold" @click="explorePrograms()">Explorar programas IBERO →</button>
    </div>

    <!-- Modal Descargando PDF -->
    <transition name="pdf-fade">
      <div v-if="isGeneratingPDF" class="pdf-modal-backdrop">
        <div class="pdf-modal-card">
          <div class="pdf-spinner-container">
            <div class="pdf-spinner"></div>
            <div class="pdf-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#633806" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="12" y1="18" x2="12" y2="12"></line>
                <polyline points="9 15 12 18 15 15"></polyline>
              </svg>
            </div>
          </div>
          <h3 class="pdf-modal-title">Descargando informe PDF</h3>
          <p class="pdf-modal-sub">Estamos preparando tu documento vocacional con todos tus resultados...</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf';
import imgIndice from '@/assets/images/pdf_bg_indice.png';
import imgQuienes from '@/assets/images/pdf_bg_quienes_somos.png';
import imgRed from '@/assets/images/pdf_bg_red.png';
import imgInteresesNuevo from '@/assets/images/pdf_bg_intereses_nuevo.png';
import imgRedExtra from '@/assets/images/pdf_bg_red_extra.png';
import imgRes1 from '@/assets/images/pdf_bg_results_1.png';
import imgRes2 from '@/assets/images/pdf_bg_results_2.png';
import imgRes3 from '@/assets/images/pdf_bg_results_3.png';
import imgRes4 from '@/assets/images/pdf_bg_results_4.png';
import logoHoriz from '@/assets/images/logo_ibero_horizontal.png';
import logoBlanco from '@/assets/images/logo_ibero_blanco.png';
import imgPortada from '@/assets/images/pdf_bg_portada.png';
import imgAcreditacion from '@/assets/images/acreditacion.png';
import imgPlanetaLogo from '@/assets/images/pdf_logo_yellow.png';

export default {
  name: 'TestResults',
  props: {
    userData: { type: Object, required: true },
    scoreData: { type: Object, required: true },
    aiAnalysis: { type: Object, required: true },
    answersText: { type: Array, required: true }
  },
  data() {
    return {
      isGeneratingPDF: false,
      RIASEC_INFO: {
        R:{name:'Técnico-Realista',short:'Técnico',color:'#185FA5',bg:'#E6F1FB'},
        I:{name:'Investigador',short:'Investigador',color:'#085041',bg:'#E1F5EE'},
        A:{name:'Artístico-Creativo',short:'Creativo',color:'#633806',bg:'#FAEEDA'},
        S:{name:'Social',short:'Social',color:'#27500A',bg:'#EAF3DE'},
        E:{name:'Empresarial',short:'Empresarial',color:'#3C3489',bg:'#EEEDFE'},
        C:{name:'Administrativo',short:'Administración',color:'#712B13',bg:'#FAECE7'},
      },
      "PROGRAMAS": {
    "R": {
      "pre": [
        "Fisioterapia",
        "Ingenieria De Sistemas",
        "Ingeniería De Software",
        "Ingeniería Industrial",
        "Seguridad Y Salud En El Trabajo",
        "Tècnico Laboral En Enfermeria"
      ],
      "pos": [
        "Maestria En Ingenieria De Software"
      ]
    },
    "I": {
      "pre": [
        "Economia",
        "Fonoaudiologia",
        "Ingenieria De Sistemas",
        "Ingeniería De Software",
        "Ingenieria En Ciencia De Datos",
        "Ingeniería Industrial",
        "Licenciatura En Ciencias Sociales",
        "Licenciatura En Matematicas",
        "Psicologia",
        "Nutricion Y Dietetica"
      ],
      "pos": [
        "Especializacion En Analitica Y Big Data",
        "Especializacion En Audiologia",
        "Especializacion En Auditoria En Salud",
        "Especializacion En Ciberseguridad",
        "Especialización En Inteligencia Artificial",
        "Especialización En Neuropsicología De La Educación",
        "Maestría En Educación",
        "Doctorado En Educacion",
        "Maestria En Ingenieria De Software",
        "Maestria En Epidemiologia"
      ]
    },
    "A": {
      "pre": [
        "Comunicacion Digital",
        "Licenciatura En Artes Visuales",
        "Licenciatura En Educacion Artistica",
        "Licenciatura En Humanidades Y Lengua Castellana",
        "Licenciatura En Lenguas Extranjeras Inglés",
        "Mercadeo Y Publicidad",
        "Diseño Visual"
      ],
      "pos": [
        "Especializacion En Docencia Universitaria",
        "Especializacion En Marketing Digital"
      ]
    },
    "S": {
      "pre": [
        "Derecho",
        "Fisioterapia",
        "Fonoaudiologia",
        "Licenciatura En Artes Visuales",
        "Licenciatura En Ciencias Sociales",
        "Licenciatura En Educacion Artistica",
        "Licenciatura En Educacion Especial",
        "Licenciatura En Educación Infantil",
        "Licenciatura En Humanidades Y Lengua Castellana",
        "Licenciatura En Lenguas Extranjeras Inglés",
        "Licenciatura En Matematicas",
        "Psicologia",
        "Seguridad Y Salud En El Trabajo",
        "Trabajo Social",
        "Licenciatura en Educación Especial V",
        "Tècnico Laboral En Enfermeria",
        "Nutricion Y Dietetica"
      ],
      "pos": [
        "Especializacion En Audiologia",
        "Especialización En Desarrollo Integral De La Infancia Y La Adolescencia",
        "Especializacion En Docencia Universitaria",
        "Especialización En Gerencia De La Seguridad Y Salud En El Trabajo",
        "Especializacion En Gerencia Del Talento Humano",
        "Especialización En Gestión De La Salud Pública",
        "Especialización En Neuropsicología De La Educación",
        "Maestría En Educación",
        "Doctorado En Educacion",
        "Especializacion en gerencia del talento humano V",
        "Maestría en Gerencia de la Seguridad y Salud en el Trabajo"
      ]
    },
    "E": {
      "pre": [
        "Administracion De Empresas",
        "Administracion En Salud",
        "Administracion Financiera",
        "Administración Logística",
        "Comunicacion Digital",
        "Derecho",
        "Marketing Y Negocios Internacionales",
        "Mercadeo Y Publicidad",
        "Negocios Internacionales",
        "Tecnología En Logística"
      ],
      "pos": [
        "Especializacion En Alta Gerencia",
        "Especialización En Gerencia De La Calidad En Salud",
        "Especialización En Gerencia De La Seguridad Y Salud En El Trabajo",
        "Especialización En Gerencia De Proyectos",
        "Especializacion En Gerencia Del Talento Humano",
        "Especializacion En Gerencia Financiera",
        "Especialización En Gestión De La Salud Pública",
        "Especialización En Gestión De Procesos Logísticos Y Redes De Valor",
        "Especializacion En Marketing Digital",
        "Maestría En Administración De Empresas - MBA",
        "Especialización en Derecho Administrativo",
        "Especializacion en gerencia del talento humano V",
        "Maestria en Gerencia de Proyectos",
        "Maestría en Gerencia de la Seguridad y Salud en el Trabajo"
      ]
    },
    "C": {
      "pre": [
        "Administracion De Empresas",
        "Administracion En Salud",
        "Administracion Financiera",
        "Administración Logística",
        "Contaduría Pública",
        "Economia",
        "Tecnología En Logística"
      ],
      "pos": [
        "Especializacion En Analitica Y Big Data",
        "Especializacion En Auditoria En Salud",
        "Especializacion En Ciberseguridad",
        "Especialización En Gerencia De La Calidad En Salud",
        "Especialización En Gerencia De Proyectos",
        "Especializacion En Gerencia Financiera",
        "Especialización En Gestión De Procesos Logísticos Y Redes De Valor",
        "Especialización en Derecho Administrativo",
        "Especializacion Revisoría Fiscal V",
        "Maestria en Gerencia de Proyectos"
      ]
    }
  },
      QS:[
        {t:'¿Cuál de estas actividades disfrutarías más en tu tiempo libre?'},
        {t:'¿En qué entorno de trabajo te sentirías más motivado?'},
        {t:'¿Qué materia del colegio disfrutabas o disfrutas más?'},
        {t:'¿Cómo prefieres resolver un problema importante?'},
        {t:'¿Qué tipo de resultado te gustaría dejar con tu carrera?'},
        {t:'¿Cuál de estas frases describe mejor cómo piensas?'},
        {t:'¿Con qué herramientas o plataformas trabajarías con más pasión?'},
        {t:'¿Cuál de estas actividades te da más energía y satisfacción?'},
        {t:'¿Cuál de estos roles ejercerías con mayor vocación y pasión?'},
        {t:'¿Qué impacto quieres generar con tu carrera profesional?'},
        {t:'¿Cuál de estas situaciones te motiva y emociona más?'},
        {t:'¿Cómo te describen mejor tus amigos, familia o compañeros?'},
        {t:'¿Dónde te ves profesionalmente dentro de 10 años?'},
        {t:'¿Cuál de estas tareas realizarías con mayor precisión y disfrute?'},
        {t:'¿Cuál de estas frases refleja tu motivación más profunda?'}
      ]
    };
  },
  computed: {
    p1() {
      return (this.scoreData && this.scoreData.top1 && this.RIASEC_INFO[this.scoreData.top1])
        || { name: 'Técnico-Realista', short: 'Técnico', color: '#185FA5', bg: '#E6F1FB' };
    },
    p2() {
      return (this.scoreData && this.scoreData.top2 && this.RIASEC_INFO[this.scoreData.top2])
        || { name: 'Investigador', short: 'Investigador', color: '#085041', bg: '#E1F5EE' };
    },
    prePrograms() {
      const top1 = this.scoreData && this.scoreData.top1;
      const list = (top1 && this.PROGRAMAS[top1] && this.PROGRAMAS[top1].pre) || [];
      return list.slice(0, 5);
    },
    posPrograms() {
      const top1 = this.scoreData && this.scoreData.top1;
      const list = (top1 && this.PROGRAMAS[top1] && this.PROGRAMAS[top1].pos) || [];
      return list.slice(0, 5);
    }
  },
  methods: {
    explorePrograms() {
      window.location.href = "https://www.ibero.edu.co/";
    },
    async genPDF() {
      this.isGeneratingPDF = true;
      await new Promise(r => setTimeout(r, 100));
      try {
        const loadImg = (src) => new Promise((resolve) => {
          const resolvedSrc = src && typeof src === 'object' && src.default ? src.default : src;
          if (!resolvedSrc) {
            resolve(null);
            return;
          }
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = (e) => {
            console.error("Error loading image:", resolvedSrc, e);
            resolve(null);
          };
          img.src = resolvedSrc;
        });

        const [
          loadedIndice, loadedQuienes, loadedRed, loadedInteresesNuevo, loadedRedExtra,
          loadedRes1, loadedRes2, loadedRes3, loadedRes4,
          loadedLogoHoriz, loadedLogoBlanco, loadedPortada, loadedAcreditacion, loadedPlaneta
        ] = await Promise.all([
          loadImg(imgIndice), loadImg(imgQuienes), loadImg(imgRed), loadImg(imgInteresesNuevo), loadImg(imgRedExtra),
          loadImg(imgRes1), loadImg(imgRes2), loadImg(imgRes3), loadImg(imgRes4),
          loadImg(logoHoriz), loadImg(logoBlanco), loadImg(imgPortada), loadImg(imgAcreditacion), loadImg(imgPlanetaLogo)
        ]);

        const doc = new jsPDF({orientation:'portrait',unit:'pt',format:'a4',compress:true});
        const W=595.28, H=841.89, M=36, CW=W-2*M;
        const {scores,sorted,top1,top2,maxVal} = this.scoreData;
        const p1=this.p1, p2=this.p2;
        const fecha=new Date().toLocaleDateString('es-CO',{day:'numeric',month:'long',year:'numeric'});
      
        const hex2rgb=h=>[parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)];
        const fc=(h)=>{const[r,g,b]=hex2rgb(h);doc.setFillColor(r,g,b)};
        const tc=(h)=>{const[r,g,b]=hex2rgb(h);doc.setTextColor(r,g,b)};  
        const stk=(h,lw=0.5)=>{const[r,g,b]=hex2rgb(h);doc.setDrawColor(r,g,b);doc.setLineWidth(lw)};
      
        function setFont(wt,sz){
          doc.setFont('helvetica', wt==='extrabold'||wt==='black'||wt==='semibold'?'bold':wt);
          doc.setFontSize(sz);
        }
      
        function wrap(txt,x,y,mw,fnt,sz,col,lead){
          setFont(fnt,sz); tc(col); if(!lead)lead=sz*1.4;
          const words=txt.split(' '); let line='', cy=y;
          for(const w of words){
            const t=(line+' '+w).trim();
            if(doc.getTextWidth(t)<=mw){line=t;}
            else{if(line)doc.text(line,x,cy);cy+=lead;line=w;}
          }
          if(line)doc.text(line,x,cy);
          return cy+lead;
        }
      
        const footer = (pg) => {
          fc('#E8E6E0'); doc.rect(0,H-25,W,25,'F');
          fc('#dfc14e'); doc.rect(0,H-25,W,1.5,'F');
          setFont('normal',7.5); tc('#7A7878');
          doc.text(`Informe vocacional IBERO · ${this.userData.name} ${this.userData.apellidos || ''}`,M,H-10);
          doc.text(`Página ${pg}`,W-M,H-10,{align:'right'});
        };
      
        const goldLine = (x,y,w=45) => {fc('#dfc14e'); doc.rect(x,y,w,2.5,'F');};
  
        const drawIberoLogo = (x = M + 115, y = 754, w = 90, h = 27) => {
          if (loadedLogoHoriz) {
            doc.addImage(loadedLogoHoriz, 'PNG', x, y, w, h);
          }
        };
      
        // ── PAGE 1 PORTADA
        if (loadedPortada) {
          doc.addImage(loadedPortada, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
      
        const fullName = `${this.userData.name} ${this.userData.apellidos || ''}`.trim().toUpperCase();
        setFont('bold', 20); tc('#FFFFFF');
        doc.text(fullName, M, 768);
        
        if (loadedLogoHoriz) {
          doc.addImage(loadedLogoHoriz, 'PNG', W - M - 110, H - 36 - 20, 110, 36);
        }
        doc.addPage();
      
        // ── PAGE 2 ÍNDICE
        if (loadedIndice) {
          doc.addImage(loadedIndice, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        const toc = [
          ['¿Quiénes somos?', 'Pág. 3'],
          ['Red Internacional de Educación Superior', 'Pág. 4'],
          ['Intereses Profesionales', 'Pág. 6'],
          ['Tu Perfil Vocacional RIASEC', 'Pág. 7'],
          ['Tus Rasgos y Fortalezas', 'Pág. 8'],
          ['Empleos del Futuro', 'Pág. 8'],
          ['Programas Recomendados', 'Pág. 9'],
          ['Registro de Respuestas', 'Pág. 10']
        ];
        toc.forEach(([title, pageNum], i) => {
          const tx = 310;
          const ty = 262 + i * 64;
          setFont('bold', 9.5); tc('#4a4848');
          doc.text(title, tx, ty);
          setFont('normal', 9.5); tc('#7A7878');
          doc.text(pageNum, W - M - 20, ty, { align: 'right' });
        });
        footer(2);
        doc.addPage();
  
        // ── PAGE 3 ¿QUIÉNES SOMOS?
        if (loadedRedExtra) {
          doc.addImage(loadedRedExtra, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        doc.addPage();

        // ── PAGE 4 ¿QUIÉNES SOMOS?
        if (loadedQuienes) {
          doc.addImage(loadedQuienes, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        doc.addPage();
  
        // ── PAGE 5 RED INTERNACIONAL
        if (loadedRed) {
          doc.addImage(loadedRed, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        doc.addPage();
        
        // ── PAGE 6 INTERESES PROFESIONALES
        if (loadedInteresesNuevo) {
          doc.addImage(loadedInteresesNuevo, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        
        setFont('bold', 32); tc('#ffffffff');
        doc.text('Intereses', M, 300);
        doc.text('profesionales', M, 340);
        goldLine(M, 360, 60);
        
        doc.addPage();
  
        // ── PAGE 7 PERFIL Y PUNTAJES (pdf_bg_results_1)
        if (loadedRes1) {
          doc.addImage(loadedRes1, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        if (loadedAcreditacion) {
          doc.addImage(loadedAcreditacion, 'PNG', M, 738, 110, 62);
        }
        drawIberoLogo(M + 115, 754, 90, 27);
        setFont('bold', 15); tc('#4a4848'); doc.text('Tu perfil vocacional RIASEC', M, 45);
        
        let py=95;
        const RW = 370;
  
        const[rb,gb,bb]=hex2rgb(p1.bg);
        doc.setFillColor(rb,gb,bb); doc.roundedRect(M,py,160,18,4,4,'F');
        setFont('bold',8.5); tc(p1.color);
        doc.text(`PERFIL: ${p1.short.toUpperCase()} + ${p2.short.toUpperCase()}`,M+8,py+12);
        py+=35;
      
        setFont('bold',14); tc('#4a4848');
        doc.text(`${this.userData.name} — ${p1.name} + ${p2.name}`,M,py);
        py+=8; goldLine(M,py,55); py+=18;
      
        const desc=this.aiAnalysis.PerfilDescripcion||`Tu perfil combina ${p1.name} y ${p2.name}.`;
        py=wrap(desc,M,py,RW,'normal',9.5,'#7A7878',14)+15;
      
        setFont('bold',10); tc('#4a4848'); doc.text('Puntuaciones RIASEC',M,py);
        py+=6; stk('#4a4848',0.7); doc.line(M,py,M+RW,py); py+=18;
      
        const barTW=RW-110, barH=10;
        sorted.forEach(([k,v])=>{
          const pct=Math.round((v/maxVal)*100); const filled=barTW*(pct/100);
          const isTop=(k===top1 || k===top2);
          setFont(isTop?'bold':'normal',9); tc(isTop?this.RIASEC_INFO[k].color:'#7A7878');
          doc.text(this.RIASEC_INFO[k].short,M,py+8);
          
          doc.setFillColor(232,230,224); doc.roundedRect(M+90,py,barTW,barH,2,2,'F');
          const[br,bg2,bb2]=hex2rgb(this.RIASEC_INFO[k].color);
          doc.setFillColor(br,bg2,bb2); if(filled>0)doc.roundedRect(M+90,py,filled,barH,2,2,'F');
          
          setFont('normal',8.5); tc('#4a4848');
          doc.text(pct+'%',M+90+barTW+15,py+8);
          py+=20;
        });
        py+=15;
      
        doc.setFillColor(245,244,240); doc.roundedRect(M,py,RW,76,4,4,'F');
        py+=20;
        [[`Estudiante`,`${this.userData.name} ${this.userData.apellidos || ''}`,M+12,py],[`Correo`,this.userData.email,M+192,py],
         [`Colegio`,this.userData.colegio,M+12,py+22],[`Grado / Ciudad`,`${this.userData.grado} · ${this.userData.ciudad}`,M+192,py+22],
         [`Documento`,(this.userData.tipo_documento||'')+' '+(this.userData.numero_documento||''),M+12,py+44],
         [`Celular`,this.userData.celular||'No registrado',M+192,py+44],
        ].forEach(([l,v,fx,fy])=>{
          setFont('bold',8.5); tc('#4a4848'); doc.text(l,fx,fy);
          setFont('normal',8.5); tc('#7A7878'); doc.text(String(v).trim().substring(0,38),fx,fy+11);
        });
        footer(6); doc.addPage();
      
        // ── PAGE 8 FORTALEZAS, RETOS Y TOP EMPLEOS (pdf_bg_results_2)
        if (loadedRes2) {
          doc.addImage(loadedRes2, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        if (loadedAcreditacion) {
          doc.addImage(loadedAcreditacion, 'PNG', M, 738, 110, 62);
        }
        drawIberoLogo(M + 115, 754, 90, 27);
        setFont('bold', 15); tc('#4a4848'); doc.text('Tus rasgos, fortalezas y empleos', M, 45);
        
        py=85;
      
        [p1,p2].forEach((prof,idx)=>{
          const[rb2,gb3,bb3]=hex2rgb(prof.bg);
          doc.setFillColor(rb2,gb3,bb3); doc.roundedRect(M,py,RW,52,4,4,'F');
          const[rc,gc,bc]=hex2rgb(prof.color);
          doc.setDrawColor(rc, gc, bc); doc.setLineWidth(1);
          doc.roundedRect(M,py,RW,52,4,4,'S');
          
          setFont('bold',10); tc(prof.color);
          doc.text(`Tipo ${prof.short} — ${prof.name}`,M+12,py+14);
          setFont('normal',8.5); tc('#4a4848');
          const rasgos=idx===0?
            ['Análisis y pensamiento crítico','Construcción y ejecución de proyectos','Orientación a resultados tangibles']:
            ['Empatía y conexión interpersonal','Creatividad e innovación','Comunicación e influencia social'];
          
          rasgos.forEach((r,ri)=>{
            const cx=ri<2?M+12:M+RW*0.5;
            const cy=ri===0||ri===2?py+28:py+40;
            doc.text(`· ${r}`,cx,cy);
          });
          py+=60;
        });
      
        py+=5;
        setFont('bold',9.5); tc('#4a4848'); doc.text('Tus fortalezas únicas',M,py);
        py+=4; stk('#4a4848',0.7); doc.line(M,py,M+RW,py); py+=12;
        const forts=(this.aiAnalysis.Fortalezas||[]).join('  ·  ');
        py=wrap(forts,M,py,RW,'normal',8.5,'#7A7878',12)+12;
      
        setFont('bold',9.5); tc('#4a4848'); doc.text('Áreas de desarrollo',M,py);
        py+=4; stk('#4a4848',0.7); doc.line(M,py,M+RW,py); py+=12;
        const retos=(this.aiAnalysis.Retos||[]).join('  ·  ');
        py=wrap(retos,M,py,RW,'normal',8.5,'#7A7878',12)+18;
        
        setFont('bold', 10); tc('#4a4848'); doc.text('Empleos del futuro recomendados (Top)', M, py);
        py+=4; stk('#4a4848', 0.7); doc.line(M, py, M+RW, py); py+=12;
        
        const jobs=this.aiAnalysis.Empleos||[];
        const topJobs = jobs.slice(0, 2);
        topJobs.forEach((job, i)=>{
          if(!job) return;
          const isTop=i===0;
          const[rb4,gb4,bb4]=isTop?hex2rgb('#FBF6E3'):hex2rgb('#F5F4F0');
          
          doc.setFillColor(rb4,gb4,bb4); doc.roundedRect(M,py,RW,88,4,4,'F');
          const accentC=isTop?'#dfc14e':'#d0ab4b';
          fc(accentC); doc.rect(M,py,4,88,'F');
          
          setFont('bold',9.5); tc('#4a4848'); doc.text((job.Titulo || '').substring(0,50),M+12,py+14);
          
          let py2=wrap((job.PorQue||'').substring(0,180)+(job.PorQue.length>180?'...':''),M+12,py+26,RW-20,'normal',8,'#7A7878',11);
          
          setFont('bold',7.5); tc('#4a4848'); doc.text('IA + Futuro:',M+12,py2+4);
          setFont('normal',7.5); tc('#7A7878'); doc.text((job.ComoCambiaTech||'').substring(0,60),M+68,py2+4);
          
          const[rgb5,gb5,bb5]=hex2rgb(isTop?'#FFFDFC':'#EDEBF9');
          doc.setFillColor(rgb5,gb5,bb5); doc.roundedRect(M+12,py2+11,RW-24,14,2,2,'F');
          setFont('bold',7.5); tc(isTop?'#633806':'#3C3489');
          doc.text(`Formación IBERO: ${(job.FormacionIbero||'').substring(0,56)}`,M+18,py2+21);
          
          py+=96;
        });
      
        footer(7); doc.addPage();
      
        // ── PAGE 9 MÁS EMPLEOS Y PROGRAMAS RECOMENDADOS (pdf_bg_results_3)
        if (loadedRes3) {
          doc.addImage(loadedRes3, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        }
        if (loadedAcreditacion) {
          doc.addImage(loadedAcreditacion, 'PNG', M, 738, 110, 62);
        }
        drawIberoLogo(M + 115, 754, 90, 27);
        setFont('bold', 15); tc('#4a4848'); doc.text('Empleos y programas recomendados', M, 45);
        
        py=85;
        
        const optionJobs = jobs.slice(2, 4);
        if(optionJobs.length > 0) {
          setFont('bold', 10); tc('#4a4848'); doc.text('Otras opciones de empleo con gran futuro', M, py);
          py+=4; stk('#4a4848', 0.7); doc.line(M, py, M+RW, py); py+=12;
          
          optionJobs.forEach((job)=>{
            if(!job) return;
            doc.setFillColor(245,244,240); doc.roundedRect(M,py,RW,88,4,4,'F');
            fc('#d0ab4b'); doc.rect(M,py,4,88,'F');
            
            setFont('bold',9.5); tc('#4a4848'); doc.text((job.Titulo || '').substring(0,50),M+12,py+14);
            
            let py2=wrap((job.PorQue||'').substring(0,180)+(job.PorQue.length>180?'...':''),M+12,py+26,RW-20,'normal',8,'#7A7878',11);
            
            setFont('bold',7.5); tc('#4a4848'); doc.text('IA + Futuro:',M+12,py2+4);
            setFont('normal',7.5); tc('#7A7878'); doc.text((job.ComoCambiaTech||'').substring(0,60),M+68,py2+4);
            
            doc.setFillColor(237,235,249); doc.roundedRect(M+12,py2+11,RW-24,14,2,2,'F');
            setFont('bold',7.5); tc('#3C3489');
            doc.text(`Formación IBERO: ${(job.FormacionIbero||'').substring(0,56)}`,M+18,py2+21);
            
            py+=96;
          });
        }
        
        py+=5;
        setFont('bold', 10); tc('#4a4848'); doc.text('Programas académicos recomendados', M, py);
        py+=4; stk('#4a4848', 0.7); doc.line(M, py, M+RW, py); py+=12;
        
        setFont('bold',8); tc('#7A7878'); doc.text('OPCIONES DE PREGRADO',M,py); py+=12;
        
        this.PROGRAMAS[top1].pre.slice(0, 4).forEach((p,i)=>{
          const isTop=i===0;
          const[rb6,gb6,bb6]=isTop?hex2rgb('#FBF6E3'):hex2rgb('#F5F4F0');
          doc.setFillColor(rb6,gb6,bb6); doc.roundedRect(M,py,RW,22,3,3,'F');
          setFont(isTop?'bold':'normal',8.5); tc('#4a4848'); doc.text(p,M+10,py+14);
          if(isTop){
            fc('#dfc14e'); doc.roundedRect(M+RW-82,py+5,76,12,2,2,'F');
            setFont('bold',6.5); tc('#4a4848'); doc.text('★ Recomendado',M+RW-79,py+13);
          }
          py+=26;
        });
      
        py+=10; setFont('bold',8); tc('#7A7878'); doc.text('OPCIONES DE POSGRADO',M,py); py+=12;
      
        this.PROGRAMAS[top1].pos.slice(0, 3).forEach((p,i)=>{
          const isTop=i===0;
          const[rb7,gb7,bb7]=isTop?hex2rgb('#EDEAF5'):hex2rgb('#F5F4F0');
          doc.setFillColor(rb7,gb7,bb7); doc.roundedRect(M,py,RW,22,3,3,'F');
          setFont(isTop?'bold':'normal',8.5); tc('#4a4848'); doc.text(p.substring(0,60),M+10,py+14);
          if(isTop){
            fc('#4a4848'); doc.roundedRect(M+RW-82,py+5,76,12,2,2,'F');
            setFont('bold',6.5); tc('#FFFFFF'); doc.text('★ Recomendado',M+RW-79,py+13);
          }
          py+=26;
        });
      
        footer(8); doc.addPage();

        // ── PAGE 10 RESPUESTAS (pdf_bg_results_4)
        if (loadedRes4) doc.addImage(loadedRes4, 'JPEG', 0, 0, W, H, undefined, 'FAST');
        if (loadedAcreditacion) doc.addImage(loadedAcreditacion, 'PNG', M, 700, 155, 88);
        drawIberoLogo(M + 160, 730, 90, 27);
        setFont('bold',15); tc('#4a4848'); doc.text('Registro de respuestas', M, 45);
        py=85;
        setFont('normal',8); tc('#7A7878');
        doc.text(`Estudiante: ${this.userData.name}  ·  Colegio: ${this.userData.colegio}`,M,py); py+=11;
        doc.text(`Email: ${this.userData.email}  ·  Celular: ${this.userData.celular||'--'}`,M,py);
        py+=8; stk('#E8E6E0',1); doc.line(M,py,M+RW,py); py+=12;
        this.answersText.forEach((ans,idx)=>{
          const rh=26;
          doc.setFillColor(...(idx%2===0?[248,247,243]:[255,255,255])); doc.rect(M,py,RW,rh,'F');
          fc('#4a4848'); doc.roundedRect(M+4,py+6,20,14,2,2,'F');
          setFont('bold',7.5); tc('#FFFFFF'); doc.text(`P${idx+1}`,M+14,py+16,{align:'center'});
          setFont('bold',8); tc('#4a4848');
          const qText=this.QS[idx]?this.QS[idx].t:`Pregunta ${idx+1}`;
          doc.text(qText.substring(0,55)+(qText.length>55?'…':''),M+30,py+11);
          setFont('normal',7.5); tc('#dfc14e');
          doc.text(('→ '+ans).substring(0,60)+(ans.length>60?'…':''),M+30,py+21);
          py+=rh+2;
        });

        // ── Reseña / Testimonio (bloque al final de página 10)
        if (this.aiAnalysis.MensajePersonal) {
          const resenaY = Math.max(py+16, 590);
          const[rb8,gb8,bb8]=hex2rgb(p1.bg);
          doc.setFillColor(rb8,gb8,bb8); doc.roundedRect(M,resenaY,RW,68,6,6,'F');
          const[rbc,gbc,bbc]=hex2rgb(p1.color);
          doc.setDrawColor(rbc,gbc,bbc); doc.setLineWidth(1.5); doc.roundedRect(M,resenaY,RW,68,6,6,'S');
          fc(p1.color); doc.rect(M,resenaY,3,68,'F');
          setFont('bold',8); tc(p1.color); doc.text('✦  Mensaje de tu orientador vocacional',M+10,resenaY+14);
          stk(p1.color,0.5); doc.line(M+10,resenaY+18,M+RW-10,resenaY+18);
          const msg=this.aiAnalysis.MensajePersonal.substring(0,220)+(this.aiAnalysis.MensajePersonal.length>220?'…':'');
          wrap(msg,M+10,resenaY+30,RW-20,'normal',8,'#4a4848',12);
        }

        footer(9); doc.addPage();

        // ── PAGE 11 CONTRAPORTADA (última) — mismo fondo que PAGE 10
        if (loadedRes4) doc.addImage(loadedRes4,'JPEG',0,0,W,H,undefined,'FAST');

        // Overlay oscuro semitransparente para legibilidad del texto
        doc.setFillColor(30,28,28); doc.setGState(new doc.GState({opacity:0.78}));
        doc.rect(0,0,W,H,'F');
        doc.setGState(new doc.GState({opacity:1}));

        // Línea dorada vertical decorativa
        fc('#dfc14e'); doc.rect(M,150,4,200,'F');

        // Texto principal "Tu futuro..."
        setFont('bold',48); tc('#FFFFFF');
        doc.text('Tu futuro',M+20,210);
        setFont('bold',48); tc('#dfc14e');
        doc.text('comienza',M+20,266);
        setFont('bold',48); tc('#FFFFFF');
        doc.text('con una',M+20,322);
        doc.text('buena elección.',M+20,378);
        goldLine(M+20,398,80);

        // Reseña personalizada en contraportada
        const cierre=`${this.userData.name}, tu perfil ${p1.short} + ${p2.short} tiene un potencial enorme en el mercado laboral del futuro. El equipo de IBERO está aquí para acompañarte en cada paso de este camino hacia tu carrera.`;
        wrap(cierre,M+20,430,W*0.65,'normal',11,'#C8C6C6',17);

        // Logo IBERO blanco
        if (loadedLogoBlanco) doc.addImage(loadedLogoBlanco,'PNG',M+20,H-M-52,100,30);
        // Planeta logo arriba del footer en contraportada
        if (loadedPlaneta) doc.addImage(loadedPlaneta,'PNG',W-M-130,H-58,130,22);
        setFont('normal',8); tc('#9A9898');
        doc.text('ibero.edu.co  ·  Bogotá, Colombia',M+20,H-M-10);

        const safeName=this.userData.name.replace(/[^a-z0-9]/gi,'_').toLowerCase();
        doc.save(`test_vocacional_ibero_${safeName}.pdf`);
      } catch (err) {
        console.error("Error generating PDF:", err);
        alert("Error al generar PDF: " + err.message);
      } finally {
        this.isGeneratingPDF = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Results ── */
.result-badge { display: inline-block; padding: 5px 14px; border-radius: 4px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 1rem; }
.result-name { font-size: 24px; font-weight: 800; color: var(--carbon); margin-bottom: .5rem; line-height: 1.2; }
.result-sub { font-size: 14px; color: var(--text-sec); line-height: 1.7; margin-bottom: 1.5rem; }

/* ── Score bars ── */
.score-section { margin-bottom: 1.5rem; }
.score-title { font-size: 11px; font-weight: 700; letter-spacing: .08em; color: var(--text-sec); text-transform: uppercase; margin-bottom: .75rem; padding-bottom: 6px; border-bottom: 1.5px solid var(--gold); }
.score-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.score-label { font-size: 12px; font-weight: 500; color: var(--text-sec); width: 108px; flex-shrink: 0; }
.score-track { flex: 1; height: 8px; background: #EAE8E2; border-radius: 4px; overflow: hidden; }
.score-fill { height: 8px; border-radius: 4px; transition: width .7s ease; }
.score-pct { font-size: 11px; font-weight: 600; color: var(--text-sec); width: 34px; text-align: right; }

/* ── AI analysis block ── */
.analysis-block { background: var(--white); border-radius: 14px; border: .5px solid var(--border); padding: 1.25rem; margin-bottom: .75rem; }
.analysis-block h4 { font-size: 13px; font-weight: 700; color: var(--carbon); margin-bottom: .75rem; display: flex; align-items: center; gap: 8px; }
.analysis-icon { width: 20px; height: 20px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.analysis-text { font-size: 13px; color: var(--text-sec); line-height: 1.75; }
.analysis-text strong { color: var(--carbon); font-weight: 600; }

/* ── Job cards ── */
.job-card { background: var(--white); border: .5px solid var(--border); border-radius: 14px; padding: 1.1rem 1.25rem; margin-bottom: .75rem; }
.job-card.featured { border: 2px solid var(--gold); background: var(--gold-bg); }
.job-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: .5rem; }
.job-title { font-size: 14px; font-weight: 700; color: var(--carbon); line-height: 1.3; flex: 1; margin-right: 8px; }
.job-badge { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 20px; white-space: nowrap; flex-shrink: 0; }
.job-badge.hot { background: var(--gold-bg); color: #633806; }
.job-badge.new { background: #EAF3DE; color: #27500A; }
.job-stat { font-size: 12px; color: var(--text-sec); margin-bottom: .25rem; }
.job-stat strong { color: var(--carbon); }

/* ── Program list ── */
.prog-list { display: flex; flex-direction: column; gap: 7px; }
.prog-item { display: flex; justify-content: space-between; align-items: center; padding: 9px 12px; border-radius: 10px; font-size: 13px; }
.prog-item.top { background: var(--gold-bg); border: .5px solid var(--gold); }
.prog-item.rest { background: #F0EEE8; }
.prog-name { font-weight: 500; color: var(--carbon); }
.prog-meta { font-size: 11px; color: var(--text-sec); }
.prog-star { font-size: 11px; font-weight: 700; color: #633806; }

/* ── Buttons ── */
.btn-primary { background: var(--carbon); color: var(--white); border: none; padding: 14px 32px; border-radius: 10px; font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: background .2s, transform .1s; letter-spacing: .02em; }
.btn-primary.gold { background: var(--gold); color: var(--carbon); }
.btn-primary.gold:hover { background: var(--gold-d); }
.btn-ghost { background: transparent; color: var(--text-sec); border: none; padding: 12px 18px; font-family: 'Montserrat', sans-serif; font-size: 14px; cursor: pointer; }
.btn-outline { background: var(--white); color: var(--carbon); border: 1.5px solid var(--carbon); padding: 12px 24px; border-radius: 10px; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: .2s; }
.btn-outline:hover { background: var(--carbon); color: var(--white); }
.btn-outline:disabled { opacity: 0.6; cursor: not-allowed; }
.cta-row { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 1rem; }

/* ── Modal Descarga PDF ── */
.pdf-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 15, 18, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.pdf-modal-card {
  background: #ffffff;
  padding: 2.25rem 2.5rem;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  text-align: center;
  max-width: 360px;
  width: 90%;
  border: 1px solid rgba(223, 193, 78, 0.4);
  animation: modalScaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes modalScaleUp {
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
}
.pdf-spinner-container {
  position: relative;
  width: 64px;
  height: 64px;
  margin: 0 auto 1.25rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pdf-spinner {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3.5px solid rgba(223, 193, 78, 0.25);
  border-top-color: #dfc14e;
  border-radius: 50%;
  animation: pdfSpin 1s linear infinite;
}
@keyframes pdfSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.pdf-icon {
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pdf-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--carbon, #2d2b2a);
  margin-bottom: 0.5rem;
}
.pdf-modal-sub {
  font-size: 13px;
  color: var(--text-sec, #666);
  line-height: 1.5;
  margin: 0;
}
.pdf-fade-enter-active, .pdf-fade-leave-active {
  transition: opacity 0.25s ease;
}
.pdf-fade-enter, .pdf-fade-leave-to {
  opacity: 0;
}
</style>
